import { useCallback } from "react";

import toast from "components/partials/toast/toast";

import type { RefineCreativesContentFormValues } from "components/modals/refine-creatives-content-modal/refine-creatives-content-modal.types";

import { useQueueCreativesRewrite } from "hooks/use-queue-creatives-rewrite";

import { getErrorMessage } from "utilities";

type UseRewriteCreativesWithWriterParams = {
  clientId: string;
  campaignId: string;
  touchpointIds?: string[];
  onSuccess?: () => void;
};

export const useRewriteCreativesWithWriter = ({
  clientId,
  campaignId,
  touchpointIds,
  onSuccess,
}: UseRewriteCreativesWithWriterParams) => {
  const { queueRewrites, isQueueing: isSubmitting } = useQueueCreativesRewrite();

  const rewriteCreatives = useCallback(
    async (values: RefineCreativesContentFormValues): Promise<boolean> => {
      try {
        const { queued, skipped } = await queueRewrites({
          clientId,
          campaignId,
          requests: [
            {
              touchpointIds,
              emailPrompt: values.creativesAdditionalInstructions?.trim() || undefined,
            },
          ],
        });

        if (queued.length === 0) {
          toast.warning({
            data: {
              title: "No creatives were rewritten",
              message: "Only draft email creatives that aren't already being written can be rewritten.",
            },
          });
          onSuccess?.();
          return true;
        }

        const queuedLabel = `${queued.length} creative${queued.length === 1 ? "" : "s"}`;

        toast.info({
          data: {
            title: "Creatives rewrite in progress",
            message:
              skipped.length > 0
                ? `Rewriting ${queuedLabel}; ${skipped.length} were skipped. A notification will be sent upon completion.`
                : `Rewriting ${queuedLabel}. A notification will be sent upon completion.`,
          },
        });

        onSuccess?.();
        return true;
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to rewrite creatives",
            message: getErrorMessage(error) || "Please try again.",
          },
        });
        return false;
      }
    },
    [campaignId, clientId, onSuccess, queueRewrites, touchpointIds],
  );

  return { rewriteCreatives, isSubmitting };
};
